import { useState, useEffect } from "react";
import { useCloud } from "./useCloud";
import PrinterFactory from "../integrations/QPrinter/PrinterFactory";
import { toast } from "react-toastify";
import store from "../store";
import { withSpan, addBreadcrumb, captureException, setSpanAttribute } from "../utils/sentry";
import { defaultPrinterPrintConfig } from "../utils/thermalPrinterConfig.js";

const SUCCESS = "success";

export const usePrinters = () => {
  const { getPrinterConfig, savePrinterConfig } = useCloud();
  const [loading, setLoading] = useState(false);

  // Fetch printers on hook initialization
  useEffect(() => {
    fetchPrinters();
  }, []);

  // Fetch all printers
  const fetchPrinters = async () => {
    return withSpan(
      {
        name: "Fetch Printers",
        op: "printer.fetch",
        function: "fetchPrinters",
      },
      async (span) => {
        setLoading(true);
        try {
          addBreadcrumb("Fetching printer configuration", "printer.fetch");
          const { status, data } = await getPrinterConfig();
          if (status === SUCCESS) {
            const printers = (data?.printers || []).map((printer) =>
              PrinterFactory.createPrinter(defaultPrinterPrintConfig(printer))
            );
            store.printers = printers;
            setSpanAttribute(span, "printer.count", printers.length);
            span?.setStatus("ok");
          }
        } catch (error) {
          console.error("Error fetching printers:", error);
          toast.error("Failed to fetch printers");
          span?.setStatus("internal_error");
          captureException(error, {
            tags: { component: "usePrinters", action: "fetchPrinters" },
          });
        }
        setLoading(false);
      }
    );
  };

  // Add a new printer
  const addPrinter = async (printerData) => {
    return withSpan(
      {
        name: "Add Printer",
        op: "printer.add",
        function: "addPrinter",
        attributes: {
          "printer.name": printerData?.name || "unknown",
          "printer.type": printerData?.type || "unknown",
        },
      },
      async (span) => {
        let printers = store.printers;
        setLoading(true);
        try {
          addBreadcrumb("Adding new printer", "printer.add", {
            printerName: printerData?.name,
            printerType: printerData?.type,
          });
          
          const newPrinter = defaultPrinterPrintConfig({
            ...printerData,
            last_updated: new Date().toISOString().split("T")[0],
            last_updated_by: "admin",
          });

          const requestBody = {
            config: {
              printers: [...printers, newPrinter],
            },
          };

          const { status } = await savePrinterConfig(requestBody);
          if (status === SUCCESS) {
            const printer = PrinterFactory.createPrinter(newPrinter);
            store.printers = [...store.printers, printer];
            toast.success("Printer added successfully");
            setSpanAttribute(span, "printer.added", true);
            span?.setStatus("ok");
            return true;
          } else {
            toast.error("Failed to add printer");
            span?.setStatus("internal_error");
            return false;
          }
        } catch (error) {
          console.error("Error adding printer:", error);
          toast.error("Failed to add printer");
          span?.setStatus("internal_error");
          captureException(error, {
            tags: { component: "usePrinters", action: "addPrinter" },
            extra: { printerName: printerData?.name },
          });
          return false;
        } finally {
          setLoading(false);
        }
      }
    );
  };

  // Delete a printer
  const deletePrinter = async (id) => {
    let printers = store.printers;
    setLoading(true);
    try {
      const requestBody = {
        config: {
          printers: printers
            .filter((printer) => printer.id !== id)
            .map((printer) => printer.toJSON()),
        },
      };

      const { status } = await savePrinterConfig(requestBody);
      if (status === SUCCESS) {
        store.printers = store.printers.filter((printer) => printer.id !== id);
        toast.success("Printer deleted successfully");
        return true;
      } else {
        toast.error("Failed to delete printer");
        return false;
      }
    } catch (error) {
      console.error("Error deleting printer:", error);
      toast.error("Failed to delete printer");
      return false;
    } finally {
      setLoading(false);
      fetchPrinters();
    }
  };

  // Update printer connection status
  const updatePrinterConnection = async (printer, isConnected) => {
    let printers = store.printers;
    try {
      const updatedPrinter = printers.find((p) => p.id === printer.id);
      updatedPrinter.setConnectionStatus(isConnected);

      const { status } = await savePrinterConfig({
        config: {
          printers: printers.map((printer) => printer.toJSON()),
        },
      });

      if (status === SUCCESS) {
        store.printers = printers;
        return true;
      } else {
        toast.error("Failed to save printer connection status");
        return false;
      }
    } catch (error) {
      console.error("Error updating printer connection:", error);
      toast.error("Failed to update printer connection");
      return false;
    }
  };

  // Search printers
  const searchPrinters = (searchTerm) => {
    let printers = store.printers;
    return printers.filter(
      (printer) =>
        printer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        printer?.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        printer?.location?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  return {
    printers: store.printers,
    loading,
    addPrinter,
    deletePrinter,
    updatePrinterConnection,
    searchPrinters,
    fetchPrinters,
  };
};
